/**
 * Hijri Calendar Conversion Utilities
 *
 * Implements the Kuwaiti algorithm for Gregorian-to-Hijri date conversion.
 * Reference: https://www.islamicfinder.org/date-converter/
 *
 * The algorithm converts Gregorian dates to Julian Day Numbers (JDN),
 * then JDN to Hijri calendar using the mathematical formulae.
 */

/**
 * Arabic month names (1-based index)
 */
const HIJRI_MONTHS_AR = [
  "محرم",      // Muharram (1)
  "صفر",       // Safar (2)
  "ربيع الأول",    // Rabi al-Awwal (3)
  "ربيع الآخر",     // Rabi al-Thani (4)
  "جمادى الأولى",   // Jumada al-Ula (5)
  "جمادى الآخرة",   // Jumada al-Akhira (6)
  "رجب",       // Rajab (7)
  "شعبان",     // Shaban (8)
  "رمضان",     // Ramadan (9)
  "شوال",      // Shawwal (10)
  "ذو القعدة",    // Dhul Qadah (11)
  "ذو الحجة",     // Dhul Hijjah (12)
];

/**
 * English month names (1-based index)
 */
const HIJRI_MONTHS_EN = [
  "Muharram",       // 1
  "Safar",          // 2
  "Rabi al-Awwal",  // 3
  "Rabi al-Thani",  // 4
  "Jumada al-Ula",  // 5
  "Jumada al-Akhira", // 6
  "Rajab",          // 7
  "Shaban",         // 8
  "Ramadan",        // 9
  "Shawwal",        // 10
  "Dhul Qadah",     // 11
  "Dhul Hijjah",    // 12
];

/**
 * Convert Gregorian date to Julian Day Number (JDN)
 * Using the formula from the Kuwaiti algorithm
 */
function gregorianToJDN(year: number, month: number, day: number): number {
  const a = Math.floor((14 - month) / 12);
  const y = year + 4800 - a;
  const m = month + 12 * a - 3;

  return (
    day +
    Math.floor((153 * m + 2) / 5) +
    365 * y +
    Math.floor(y / 4) -
    Math.floor(y / 100) +
    Math.floor(y / 400) -
    32045
  );
}

/**
 * Convert Julian Day Number to Hijri date
 * Using the Kuwaiti algorithm (inverse operation)
 */
function jdnToHijri(jdn: number): { year: number; month: number; day: number } {
  const N = jdn - 1948440 + 0.5;
  const q = Math.floor(N / 10631);
  const r = N % 10631;
  const a = Math.floor(r / 325);
  const w = Math.floor((a * 325 + 325) / 354);
  const b = Math.floor(((w * 11) + 3) / 30);
  const z = r - a * 325 + Math.floor((a * 325) / 354) - Math.floor((b * 354) / 11);
  const e = Math.floor(((z + 30.5) % 354 + 0.5) / 29.5001);
  const j = z + Math.floor((e * 354.36667) / 12);

  const year = 30 * q + 11 * a + w + 1;
  const month = (j + 1) % 13;
  const day = Math.floor(((z - a * 325 + Math.floor((a * 325) / 354) - Math.floor((b * 354) / 11)) % 30)) + 1;

  return {
    year,
    month: month === 0 ? 12 : month,
    day,
  };
}

/**
 * Hijri date object
 */
export interface HijriDate {
  year: number;
  month: number; // 1-12
  day: number;   // 1-30
}

/**
 * Convert a Gregorian Date to Hijri calendar
 * @param date JavaScript Date object or null
 * @returns Hijri date object with year, month (1-12), day, and formatted string
 */
export function gregorianToHijri(date: Date | string | null): HijriDate & { formatted: string } {
  if (!date) {
    return { year: 0, month: 0, day: 0, formatted: "" };
  }

  const d = typeof date === "string" ? new Date(date) : date;

  if (isNaN(d.getTime())) {
    return { year: 0, month: 0, day: 0, formatted: "" };
  }

  const year = d.getFullYear();
  const month = d.getMonth() + 1; // JS months are 0-based
  const day = d.getDate();

  const jdn = gregorianToJDN(year, month, day);
  const hijri = jdnToHijri(jdn);

  return {
    ...hijri,
    formatted: `${hijri.day} ${HIJRI_MONTHS_EN[hijri.month - 1]} ${hijri.year}`,
  };
}

/**
 * Format a Hijri date in the specified locale
 * @param hijri Hijri date object with year, month (1-12), day
 * @param locale 'ar' for Arabic, 'en' for English
 * @returns Formatted string (e.g., "15 Rajab 1447" or "١٥ رجب ١٤٤٧")
 */
export function formatHijriDate(
  hijri: { year: number; month: number; day: number },
  locale: "ar" | "en" = "en"
): string {
  if (!hijri || hijri.month < 1 || hijri.month > 12 || hijri.day < 1 || hijri.day > 30) {
    return "—";
  }

  const monthName = locale === "ar"
    ? HIJRI_MONTHS_AR[hijri.month - 1]
    : HIJRI_MONTHS_EN[hijri.month - 1];

  if (locale === "ar") {
    // Convert numbers to Arabic-Indic numerals
    const arabicDay = String(hijri.day).replace(/\d/g, (d) => String.fromCharCode(0x0660 + parseInt(d)));
    const arabicYear = String(hijri.year).replace(/\d/g, (d) => String.fromCharCode(0x0660 + parseInt(d)));
    return `${arabicDay} ${monthName} ${arabicYear}`;
  }

  return `${hijri.day} ${monthName} ${hijri.year}`;
}
